import { useState, useEffect, useContext } from 'react';
import IconButton from '@mui/material/IconButton';
import CloseIcon from '@mui/icons-material/Close';

import UploadWorker from 'app/workers/UploadWorker';
import ProgressBar from 'app/components/ProgressBar';
import classes from 'app/components/DataGrid/classes.module.scss';

// this function matches the one in public/workers/portal/upload_worker.js
const testMatchFileContext = (fileContext, matchFileContext) => {
  // check that fileContext includes matchFileContext.
  // if a field is an array, then the fileContext's field
  // should contain those elements
  for (let field in matchFileContext) {
    if (!fileContext[field]) {
      return false;
    }

    let value = matchFileContext[field];

    if (Array.isArray(value)) {
      if (!Array.isArray(fileContext[field])) {
	return false;
      }

      for (let e of value) {
	if (fileContext[field].indexOf(e) === -1) {
	  return false;
	}
      }
    }
  }

  return true;
}

const UploadProgressBar = props => {
  // this progress bar can track an individual file or a collection.
  // with each event it will receive a fileContext,
  // which is built and passed to useUploadForm by the outer hook,
  // ex: useMomentsUploadForm.
  // to match an individual file, a fileId prop can be passed
  // (for moment uploads, this would be the moment ID).
  // for collections of files, a matchFileContext object can be passed
  // to match against events, and it will be passed to the uploader
  // for querying the collective progress of the fileContexts that match
  // this can be used to track any arbitrary collection of files.
  // for example, to track all of the uploads for a job,
  // this matchFileContext can be passed:
  //
  // const matchFileContext = {
  //   story_id: job.template_story_id
  // }
  //
  // the upload worker will handle aggregating the files and responding
  // with the accumulated progress to the query requests.
  // this component will otherwise rely on the incoming events
  // to increment the progress bar and determine completion.
  const {
    // fileId can be passed to track individual file uploads
    fileId,

    // required for collections, fileContext or groupContext to match files in the uploader
    matchFileContext,

    // optionally seed the progress bar (the events will update it).
    // note that for individual files, these values will be in bytes,
    // but for collections they will be based on file counts
    total,
    loaded,

    // originally this progress bar component was created for the data grid,
    // but it's since been added to the modal.
    // this inModal prop is a quick fix to style it within the modal
    inModal
  } = props;

  const uploadWorker = useContext(UploadWorker);

  const stopUpload = () => {
    if (fileId) {
      uploadWorker.abortFileUpload(fileId);
    } else {
      uploadWorker.abortGroupUpload(matchFileContext);
    }
  }

  const calculateProgress = (total, loaded) => {
    return parseInt(((loaded || 0) / total) * 100);
  }

  const initialProgress = total ? calculateProgress(total, loaded) : 0;

  const [state, setState] = useState({
    total: total || 0,
    loaded: loaded || 0,
    progress: initialProgress,
    error: null,
    complete: initialProgress >= (total || 0)
  });

  const setProgress = (total, loaded) => {
    let progress = calculateProgress(total, loaded);
    let complete = progress === 100;

    setState(state => ({
      ...state,
      total,
      loaded,
      progress,
      complete
    }));
  }

  useEffect(() => {
    const onUnmount = uploadWorker.onMessage(event => {
      // if not progress or error event then skip
      if (['progress', 'error'].indexOf(event.data.type) === -1) {
        return;
      }

      let fileContext = event.data;

      // if we're tracking a file and this isn't it, then skip
      if (fileId && fileId !== fileContext.id) {
	return;
      }

      // if we're tracking a collection and this isn't it, then skip
      let isCollection = !fileId;
      if (isCollection && !testMatchFileContext(fileContext, matchFileContext)) {
	return;
      }

      switch (event.data.type) {
	case 'progress':
	  // if we're tracking a collection,
	  // the we need to query the worker for its progress.
	  // otherwise we can just use the values provided in the event
	  if (isCollection) {
	    uploadWorker.getStatus(matchFileContext, status => {
	      let {
		total,
		loaded
	      } = status;

	      setProgress(total, loaded);
	    });

	  } else {
	    let {
	      total,
	      loaded
	    } = event.data;

	    setProgress(total, loaded);
	  }
	  break;

	case 'error':
	  setState(state => ({
	    ...state,
	    error: event.data.error
	  }));
	  break;
      }
    });

    return onUnmount;
  }, []);

  if (state.error) {
    return <span className={classes.gridCell} title={state.error}>Failed</span>;
  }

  // hide if in modal and not active
  if (inModal && (!state.loaded || state.complete)) {
    return null;
  }

  //if (state.complete) {
  //  return <span className={classes.gridCell}>Complete</span>;
  //}

  let count = `${state.progress}%`;
  let width = inModal ? '100%' : '80%';
  let textMargin = inModal ? 0 : '1rem 0 0';
  let spacer = inModal ? <br /> : null;
  let iconMarginTop = inModal ? 0 : '1.5rem';

  return (
    <div style={{ display: 'flex' }}>
      <div style={{ width }}>
        <p style={{ margin: textMargin }}>Uploading</p>
        <ProgressBar label={count} progress={state.progress} />
        {spacer}
      </div>
      <IconButton title="Stop Upload" aria-label="Stop Upload" onClick={stopUpload} sx={{ marginTop: iconMarginTop }}>
        <CloseIcon sx={{ color: '#000' }} />
      </IconButton>
    </div>
  );
}

export default UploadProgressBar;
